import { catchError, filter, map, share, shareReplay, switchMap, take } from 'rxjs/operators';
import { Injectable } from '@angular/core';
import {
  Addon,
  AddonsListResponse,
  ListAddonsRequest,
  MarketplaceApp,
  MarketplaceAppsApiService,
} from '@galaxy/marketplace-apps/v1';
import { Observable, of, ReplaySubject } from 'rxjs';
import { MarketplaceAppService } from './marketplace-app/marketplace-app.service';
import { SnackbarService } from '@vendasta/galaxy/snackbar-service';

@Injectable()
export class AddonService {
  private currentAddonId$$: ReplaySubject<string> = new ReplaySubject<string>(1);
  private currentAddon$$: ReplaySubject<MarketplaceApp> = new ReplaySubject<MarketplaceApp>(1);

  constructor(
    private marketplaceAppsService: MarketplaceAppsApiService,
    private appService: MarketplaceAppService,
    private snackbarService: SnackbarService,
  ) {
    this.currentAddonId$
      .pipe(
        switchMap((addonId) => {
          if (!addonId) {
            return of(null);
          }
          return this.appService.getDraft(addonId).pipe(
            take(1),
            catchError(() => {
              this.snackbarService.openErrorSnack('Error loading addon. Please try again');
              return of(null);
            }),
          );
        }),
      )
      .subscribe((addon) => this.currentAddon$$.next(addon));
  }

  get currentAddonId$(): Observable<string> {
    return this.currentAddonId$$.asObservable().pipe(share());
  }

  get currentAddon$(): Observable<MarketplaceApp> {
    return this.currentAddon$$.asObservable().pipe(filter((a) => !!a));
  }

  set currentAddon(addon: MarketplaceApp) {
    this.currentAddon$$.next(addon);
  }

  set currentAddonId(addonId: string) {
    this.currentAddonId$$.next(addonId);
  }

  public list(appId: string): Observable<Addon[]> {
    const request = new ListAddonsRequest();
    request.appId = appId;
    return this.marketplaceAppsService.listAddons(request).pipe(
      map((response: AddonsListResponse) => {
        const addons = response.addons as Addon[];
        if (addons) {
          return addons.sort((a, b) => (a.title || '').localeCompare(b.title));
        }
        return [];
      }),
      shareReplay(1),
    );
  }
}
